<script setup lang="ts">
import type {UserData} from "~/types/UserData";

const props = defineProps<{
  author: UserData
}>();

const toast = useToast();
const {refreshIdentity} = useSanctumAuth();
const selectedAmount = ref<number | null>(null);
const isConfirming = ref(false);
const isSuccess = ref(false);
const isLoading = ref(false);

function selectAmount(amount: number) {
  selectedAmount.value = amount;
  isConfirming.value = true;
  isSuccess.value = false;
}

function cancel() {
  selectedAmount.value = null;
  isConfirming.value = false;
}

async function confirmDonation() {
  if (!selectedAmount.value) return;

  isLoading.value = true;
  try {
    await $api("/api/donations", {
      method: "POST",
      body: {
        amount: selectedAmount.value,
        recipient_uuid: props.author.uuid,
      },
    });
    await refreshIdentity();
    isSuccess.value = true;
    isConfirming.value = false;
    selectedAmount.value = null;
  } catch (error: any) {
    console.error("Erreur lors de l'envoi du don", error);
    toast.add({
      title: "Erreur",
      description: "Une erreur est survenue lors de l'envoi de ton soutien. Peux-tu réessayer ?",
      color: "error",
    });
  } finally {
    isLoading.value = false;
  }
}
</script>

<template>
  <UCard class="border-0 bg-gradient-scribs">
    <h3 class="text-xl underline mb-4">Soutenir cette auteur•rice</h3>
    <div class="mb-4">
      <p v-if="author.scribs_donors_count && author.scribs_donors_count > 0">
        <span class="font-semibold">{{ author.scribs_donors_count }}</span> lectrices et lecteurs soutiennent son travail.
      </p>
      <p v-else>
        Soyez le premier à soutenir cet auteur !
      </p>
    </div>
    <div class="grid grid-cols-2 gap-4">
      <UButton
          class="justify-center"
          color="secondary"
          variant="solid"
          :disabled="isLoading"
          @click="selectAmount(5)"
          icon="i-custom-coin"
      >
        5 scribs
      </UButton>
      <UButton
          class="justify-center"
          color="secondary"
          variant="solid"
          :disabled="isLoading"
          @click="selectAmount(10)"
          icon="i-custom-coin"
      >
        10 scribs
      </UButton>
      <UButton
          class="justify-center"
          color="secondary"
          variant="solid"
          :disabled="isLoading"
          @click="selectAmount(25)"
          icon="i-custom-coin"
      >
        25 scribs
      </UButton>
      <UButton
          class="justify-center"
          color="secondary"
          variant="solid"
          :disabled="isLoading"
          @click="selectAmount(50)"
          icon="i-custom-coin"
      >
        50 scribs
      </UButton>
    </div>

    <div v-if="isConfirming" class="mt-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
      <p class="mb-4">Tu es sur le point de soutenir cet auteur•rice avec {{ selectedAmount }} scribs.</p>
      <div class="flex gap-2">
        <UButton
            color="primary"
            variant="solid"
            :loading="isLoading"
            @click="confirmDonation"
        >
          Confirmer
        </UButton>
        <UButton
            color="neutral"
            variant="outline"
            :disabled="isLoading"
            @click="cancel"
        >
          Annuler
        </UButton>
      </div>
    </div>

    <div v-if="isSuccess" class="mt-4 p-4">
      Merci pour ton soutien ! Nous en informons l'auteur.e.
    </div>
  </UCard>
</template>
